import { Account, KnowledgeSpaceReference } from '../types'
interface SearchResult {
  type: 'account' | 'space'
  id: string
  name: string
  subtitle: string
  relevanceScore: number
  matchedTokens: string[]
}
/**
 * Fuzzy match scoring - returns 0-100
 */
function fuzzyScore(query: string, text: string): number {
  const q = query.toLowerCase()
  const t = text.toLowerCase()
  if (t === q) return 100
  if (t.includes(q)) return 80
  if (t.startsWith(q)) return 90
  // Character-by-character fuzzy match
  let score = 0
  let queryIndex = 0
  for (let i = 0; i < t.length && queryIndex < q.length; i++) {
    if (t[i] === q[queryIndex]) {
      score += 10
      queryIndex++
    }
  }
  return queryIndex === q.length ? score : 0
}
/**
 * Extract matched tokens for highlighting
 */
function extractMatchedTokens(query: string, text: string): string[] {
  const q = query.toLowerCase()
  const words = text.split(/\s+/)
  return words.filter((word) => word.toLowerCase().includes(q))
}
/**
 * Smart search with role-based boosting
 */
export function smartSearch(
  query: string,
  accounts: Account[],
  userRole: string,
  ownedAccountIds: string[],
): SearchResult[] {
  if (!query.trim()) return []
  const results: SearchResult[] = []
  accounts.forEach((account) => {
    let relevanceScore = 0
    const matchedTokens: string[] = []
    // Score account name
    const nameScore = fuzzyScore(query, account.name)
    relevanceScore += nameScore * 2 // Name is most important
    if (nameScore > 0) {
      matchedTokens.push(...extractMatchedTokens(query, account.name))
    }
    // Score industry
    const industryScore = fuzzyScore(query, account.industry)
    relevanceScore += industryScore
    if (industryScore > 0) {
      matchedTokens.push(...extractMatchedTokens(query, account.industry))
    }
    // Score products/services
    account.products_services.forEach((service) => {
      const serviceScore = fuzzyScore(query, service)
      relevanceScore += serviceScore * 0.5
      if (serviceScore > 0) {
        matchedTokens.push(...extractMatchedTokens(query, service))
      }
    })
    // Score region
    const regionScore = fuzzyScore(query, account.region)
    relevanceScore += regionScore * 0.3
    if (regionScore > 0) {
      matchedTokens.push(...extractMatchedTokens(query, account.region))
    }
    // Boost for owned accounts
    if (ownedAccountIds.includes(account.id)) {
      relevanceScore *= 1.5
    }
    // Boost for Account Admins viewing their accounts
    if (userRole === 'Account Admin' && ownedAccountIds.includes(account.id)) {
      relevanceScore *= 1.3
    }
    if (relevanceScore > 0) {
      results.push({
        type: 'account',
        id: account.id,
        name: account.name,
        subtitle: `${account.industry} • ${account.region}`,
        relevanceScore,
        matchedTokens: [...new Set(matchedTokens)],
      })
    }
  })
  return results.sort((a, b) => b.relevanceScore - a.relevanceScore)
}
/**
 * Search Knowledge Spaces
 */
export function searchSpaces(
  query: string,
  accounts: Account[],
): SearchResult[] {
  if (!query.trim()) return []
  const results: SearchResult[] = []
  const spacesMap = new Map<
    string,
    KnowledgeSpaceReference & { accountName: string }
  >()
  // Collect all unique spaces
  accounts.forEach((account) => {
    account.bots.forEach((bot) => {
      bot.bound_spaces.forEach((space) => {
        if (!spacesMap.has(space.id)) {
          spacesMap.set(space.id, { ...space, accountName: account.name })
        }
      })
    })
  })
  spacesMap.forEach((space) => {
    let relevanceScore = 0
    const matchedTokens: string[] = []
    // Score space name
    const nameScore = fuzzyScore(query, space.name)
    relevanceScore += nameScore * 2
    if (nameScore > 0) {
      matchedTokens.push(...extractMatchedTokens(query, space.name))
    }
    // Score account name
    const accountScore = fuzzyScore(query, space.accountName)
    relevanceScore += accountScore
    if (accountScore > 0) {
      matchedTokens.push(...extractMatchedTokens(query, space.accountName))
    }
    // Score visibility
    const visibilityScore = fuzzyScore(query, space.visibility)
    relevanceScore += visibilityScore * 0.5
    if (relevanceScore > 0) {
      results.push({
        type: 'space',
        id: space.id,
        name: space.name,
        subtitle: `${space.visibility} • ${space.accountName}`,
        relevanceScore,
        matchedTokens: [...new Set(matchedTokens)],
      })
    }
  })
  return results.sort((a, b) => b.relevanceScore - a.relevanceScore)
}
/**
 * Highlight matched tokens in text
 */
export function highlightMatches(text: string, tokens: string[]): string {
  if (tokens.length === 0) return text
  let highlighted = text
  tokens.forEach((token) => {
    const regex = new RegExp(`(${token})`, 'gi')
    highlighted = highlighted.replace(regex, '<mark>$1</mark>')
  })
  return highlighted
}
